import { useState } from "react";
import { CheckCircle } from "lucide-react";
import { useCentralStore } from "../../../../../dentlab/src/store/Central";
import { formatClientNameForSidebar } from "@/lib/utils/client-utils";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Textarea } from "../../ui/textarea";
import { Button } from "../../ui/button";

/**
 * Note: Can only be used within ClientContext
 * @param shared - whether the entity is shared or not, parent should use [patient/job/file].shared_with to determine this
 * @param entityName - name of the entity to be shared, e.g. "Patient", "Auftrag", "Datei"
 * @param onShare - called when entity is shared
 * @param onUnshare - called when entity is unshared
 * @param onNotify - called when notification is sent
 * @param open - whether the dialog is open or not
 * @param onClose - called when dialog is closed
 */
export const ShareDialog: React.FC<{
	shared: boolean;
	entityName: string | null;
	onShare: () => void;
	onUnshare: () => void;
	onNotify: (message: string) => void;
	open: boolean;
	onClose: () => void;
}> = (props) => {
	const { open, onClose, shared } = props;
	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
			<DialogContent>
				{shared ? (
					<ShareDialogScreenShared {...props} />
				) : (
					<ShareDialogScreenNotShared {...props} />
				)}
			</DialogContent>
		</Dialog>
	);
};

// if shared
// handles onUnshare, onNotify
export const ShareDialogScreenShared: React.FC<{
	onShare: () => void;
	onNotify: (message: string) => void;
	open: boolean;
	onClose: () => void;
}> = ({ onNotify, onClose }) => {
	const { client } = useCentralStore((state) => ({
		client: state.client,
	}));
	const [notificationText, setNotificationText] = useState("");

	const handleNotify = () => {
		onNotify(notificationText);
	};

	return (
		<>

			<DialogHeader className="flex items-center justify-center flex-col min-h-24 py-4">
				<CheckCircle className="text-green-600 h-10 w-10 mb-2" />
				<DialogTitle className="text-2xl text-green-600 font-semibold">
					Geteilt mit {formatClientNameForSidebar(
						client?.first_name ?? null,
						client?.last_name ?? null,
						client?.company_name ?? null,
						client?.is_company ?? null
					)}
				</DialogTitle>
			</DialogHeader>
			<DialogDescription>
				<h3 className="text-lg font-semibold text-black">
					Personen per Email benachrichtigen
				</h3>
				<div className="pt-2 flex flex-col space-y-4">
					<Textarea
						placeholder="Nachricht"
						rows={4}
						value={notificationText}
						onChange={(event) => {
							setNotificationText(event.target.value);
						}}
						aria-label="Benachrichtigungsnachricht"
						data-testid="notification-message-textarea"
					/>
				</div>

			</DialogDescription>
			<DialogFooter>
				<Button
					variant="ghost"
					onClick={() => onClose()}
					aria-label="Keine Nachricht senden"
					data-testid="cancel-notification-button"
				>
					Keine Nachricht senden
				</Button>
				<Button
					onClick={handleNotify}
					aria-label="Benachrichtigung senden"
					data-testid="send-notification-button"
				>
					Bestätigen
				</Button>
			</DialogFooter>
		</>
	);
};

// if not shared
// handles onShare
// only files are explicitly shared, so we can stop making this super generic
export const ShareDialogScreenNotShared: React.FC<{
	entityName: string | null;
	onShare: () => void;
}> = (props) => {
	const { entityName, onShare } = props;
	return (
		<>
			<DialogHeader>
				<DialogTitle>{entityName} freigeben</DialogTitle>
			</DialogHeader>
			<DialogDescription className="text-base">
				Alle Behandler, die dem Auftraggeber zugeordnet sind, können
				über ihren Account sicher auf den Inhalt zugreifen.
			</DialogDescription>
			<DialogFooter>
				<Button
					onClick={onShare}
					aria-label="Freigabe bestätigen"
					data-testid="confirm-share-button"
				>
					Bestätigen
				</Button>
			</DialogFooter>
		</>
	);
};
